import Vimeo from "@u-wave/react-vimeo";
import Image from "next/image";
import Link from "next/link";
import { useEffect } from "react";
import ProductPopup from "../../components/Parts/ProductPopup";
import {useState} from "react";
import { Accordion } from "react-bootstrap";
import BreadcrumbComponent from "../../components/Common/BreadcrumbComponent";
import ExtraPageFooter from "../../components/Common/ExtraPageFooter";
import ProductGalleryComponent from "../../components/Common/ProductGalleryComponent";
import useTrait from "../../components/Common/syncUpdateState";
import Footer from "../../components/_App/Footer";
import Header from "../../components/_App/Header";
import { STOCK_LIST_QUERY, STOCK_QUERY } from "../../data/stockQuery";
import queryWithRetry from "../../data/queryWithRetry";
import * as ga from "../../lib/ga";
import { slugify } from "../../utils/helpers";
import SectionsRender from "../../components/Common/SectionsRender";
import cacheClient from "../../data/cacheClient";
import { PHASE_PRODUCTION_BUILD } from "next/constants";
import StatusTooltip from "../../components/Common/StatusTooltip";
import { useUTMTracking } from "../../utils/useUTMTracking";

function Part({ data, id, seo, menu, footer,stockPage,pageLinkUrl,detailsType }) {
  const isAU = process.env.NEXT_PUBLIC_SITE_IS_AU == "true";
  const isProduction = process.env.NODE_ENV === "production";
  const downloadFilter = isAU ? (x) => x.isAU : (x) => x.isNZ;
  const stockCondition = isAU ? data.stockConditionAU : data.stockConditionNZ;
  const publishMajorApplication = (data.publishMajorApplication && data.webUses);
  const showChannelAdvisorBuyLink = isAU ? data.showChannelAdvisorBuyLink : false; //we dont show for NZ anymore data.showChannelAdvisorBuyLinkNZ;
  const caCampaignId = isAU? data.channelAdvisorCampaignIdAU : data.channelAdvisorCampaignIdNZ;
  
  // Only show buy buttons if stock condition is available and not null
  const isProductAvailable = stockCondition && stockCondition !== 'Hidden' && stockCondition !== null;

  // Initialize UTM tracking (simplified - cleanup handled globally in _app.js)
  const utmTracking = useUTMTracking({
    autoCapture: true,
    maxAge: 30 * 60 * 1000, // 30 minutes
    persistAcrossRoutes: true
  });

  const [showModal, setShowModal] = useState(false);
  const [detailsUrl, setDetailsUrl]=useState(pageLinkUrl ? (pageLinkUrl.startsWith('/') ? pageLinkUrl : '/' + pageLinkUrl) : null);
  const [initialUTMSource, setInitialUTMSource] = useState(null);
  const [isClient, setIsClient] = useState(false);

  // Initialize client-side state and capture initial UTM parameters
  useEffect(() => {
    setIsClient(true);
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const utmSource = urlParams.get('utm_source');
      setInitialUTMSource(utmSource);
    }
  }, []);

  // Check if BUY NOW button should be hidden based on UTM session data
  // Hide button if UTM source is 'autob' in stored session data
  const shouldHideBuyNowButton = utmTracking.utmParams?.utm_source === 'autob';

  useEffect(() => {
    if (isProduction) {
      ga.event({
        action: id,
        params: {
          event_category: "View Part",
        },
      });
    }
  }, [isProduction, id]);  
  
  if (data === undefined) {
    // console.log("data is empty for id ", id);
    return <></>;
  }

  let typeName = "";
  if (data["typeName"] !== undefined) {
    typeName = data["typeName"];
  }

  const breadcrumb = {
    breadcrumbs: [
      { title: "Product" },
      { title: typeName },
      { title: data.stockCode },
    ],
  };

  let desc = data.longDescription;
  if (desc == null || desc == "") {
    desc = data.description;
  }

  let finalSpec = [];
  if (data?.specifications) {
    finalSpec = data.specifications.filter((item) => {
      let val = String(item.value);
      if (item.skipIfEmptyOrZero) {
        if (val == "" || item.value == 0) {
          return false;
        }
      }
      if (val.length > 0) {
        return true;
      } else {
        return false;
      }
    });
  }
  //console.log(finalSpec);  

  const showFitmentGuides =
    data?.fitmentGuides &&
    (data.fitmentGuides.length > 0 || data.fitmentLocations?.length > 0);

  //console.log("product data", data);

  const kitContents = data?.kitContent ?? null;

  function* fitmentLocationsGenerator(list) {
    if (list) {
      for (let i = 0; i < list.length; i++) {
        const item = list[i];
        yield { title: "Location", value: item.location };
        yield { title: "Installation Time", value: item.time };
      }
    }
  }
  
  let showKitContentsInterchangeColumn = false;
  //find to see if any child lines has interchange value, else dont display that column
  if (kitContents!=null && kitContents.contents!=null)
    for (var i = 0; i < kitContents.contents.length; i++) {      
      const item = kitContents.contents[i];
      if (item.interchange) {
        showKitContentsInterchangeColumn = true;
        break;
      }
    }

  const fitmentLocations = Array.from(
    fitmentLocationsGenerator(data?.fitmentLocations)
  );

  const extraInfo = data?.extraInfo ?? {};
  let extraDownloads = extraInfo.downloads ?? [];
  extraDownloads = extraDownloads.filter(downloadFilter);
  const showExtraInfo = extraDownloads.length > 0;

  let featureBenefits = data?.features ?? [];  
  let bespoke = data?.bespokeFeatures ?? [];
  //if(pageLinkUrl!=null)
  // console.log("page_Link_Url", detailsUrl);
  //console.log(showExtraInfo, extraDownloads);

  return (
    <>
      <Header {...menu}></Header>
      <div className='page-wrapper flex-grow-1'>
        <BreadcrumbComponent {...breadcrumb}></BreadcrumbComponent>
        <div className='product-detail-section container new-space py-md-3 py-0 py-lg-5 mb-md-3 mb-0 mb-lg-5'>
          <div className='pd-body'>
            <div className='row'>
              <div className='col-sm-6 py-md-0 py-3'>
                <ProductGalleryComponent {...data}></ProductGalleryComponent>
              </div>
              <div className='col-sm-6'>
                <h1>
                  {data.stockCode} {data.webDescription}
                </h1>
                <p className='lead'>{desc}</p>
                <div className='mb-3 mb-md-5 d-md-block'>
                  {isClient && !shouldHideBuyNowButton && !showChannelAdvisorBuyLink && isProductAvailable && (
                    <Link href='/-/where-to-buy' legacyBehavior>
                      <a className='btn btn-primary'>Where to buy</a>
                    </Link>
                  )}
                  {isClient && !shouldHideBuyNowButton && showChannelAdvisorBuyLink && isProductAvailable && (
                    <button
                      className='btn btn-primary'
                      onClick={() => setShowModal(true)}
                    >
                      BUY NOW
                    </button>
                  )}
                  
                                   
                     {showModal && data && (
                     <ProductPopup
                       show={showModal}
                       partNo={data.stockCode}
                       campaignId={caCampaignId}
                       data={data}
                       handleClose={() => setShowModal(false)}
                     />
                   )}
                </div>
                <Accordion alwaysOpen>
                  {/* Availability Section */}
                  <Accordion.Item eventKey='0'>
                    <Accordion.Header>Availability</Accordion.Header>
                    <Accordion.Body>
                      <table className='table table-striped info-table'>
                        <tbody>
                          <tr>
                            <th style={{ textAlign: 'left', paddingLeft: '15px' }}>Status</th>
                            <td className="position-relative" style={{ textAlign: 'right', paddingRight: '15px' }}>
                              <StatusTooltip status={stockCondition}>
                                <span style={{ textAlign: 'right', paddingRight: '20px' }}>{stockCondition}</span>
                              </StatusTooltip>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </Accordion.Body>
                  </Accordion.Item>
                  
                  {featureBenefits.length > 0 && (
                    <Accordion.Item eventKey='1'>
                      <Accordion.Header>Features and Benefits</Accordion.Header>
                      <Accordion.Body>
                        <ul className='checklist'>
                          {featureBenefits.map(function (item, idx) {
                            return (
                              <li key={"checkList" + idx}>{item.feature}</li>
                            );
                          })}
                          {bespoke &&
                            bespoke.map(function (item, idx) {
                              return (
                                <li key={"checkList" + idx}>{item.feature}</li>
                              );
                            })}
                        </ul>
                      </Accordion.Body>
                    </Accordion.Item>
                  )}
                  
                  {finalSpec.length > 0 && (
                    <Accordion.Item eventKey='2'>
                      <Accordion.Header>Tech Specs</Accordion.Header>
                      <Accordion.Body>
                        <table className='table table-striped info-table'>
                          <tbody>
                            {finalSpec.map(function (item, idx) {
                              let val = String(item.value);
                              if (val !== "Hidden") {
                                if (val == "true") {
                                  val = "Yes";
                                } else if (val == "false") {
                                  val = "No";
                                }
                                return (
                                  <tr key={"spec" + idx}>
                                    <th>{item.label}</th>
                                    <td>
                                      <span>{val} {item.suffix}</span>
                                    </td>
                                  </tr>
                                );
                              }
                            })}
                          </tbody>
                        </table>
                       
                      </Accordion.Body>
                    </Accordion.Item>
                  )}
                   
                  {showFitmentGuides && (
                    <Accordion.Item eventKey='3'>
                      <Accordion.Header>Fitment Guide</Accordion.Header>
                      <Accordion.Body>
                        <table className='table table-striped info-table'>
                          <tbody>
                            {fitmentLocations.map(function (item, idx) {
                              return (
                                <tr key={"ft" + idx}>
                                  <th>{item.title}</th>
                                  <td>{item.value}</td>
                                </tr>
                              );
                            })}
                            {data.fitmentGuides &&
                              data.fitmentGuides.map(function (item, idx) {
                                return (
                                  <tr key={"ftg" + idx}>
                                    <th>Fitment Guide</th>
                                    <td>
                                      <a
                                        href={item.url}
                                        target='_blank'
                                        rel='noreferrer'
                                      >
                                        {item.title}
                                        <Image
                                          src='/icons/download.svg'
                                          width='14'
                                          height='14'
                                          alt='Download Fitment Guide'
                                          layout='raw'
                                        ></Image>
                                      </a>
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </Accordion.Body>
                    </Accordion.Item>
                  )}
                  {kitContents && kitContents.contents && (
                    <Accordion.Item eventKey='4'>
                      <Accordion.Header>Kit Contents</Accordion.Header>
                      <Accordion.Body>
                        <div className='text-center'>
                          {kitContents.modelDescription}{" "}
                          {kitContents.interchangeableWith
                            ? "Interchangeable with "
                            : " "}
                          {kitContents.interchangeableWith}
                        </div>
                        <table className='table table-striped'>
                          <thead className='text-center'>
                            <tr>
                              <td>Ryco Part</td>
                              <td>Description</td>
                              {showKitContentsInterchangeColumn && (
                                <td>Interchangeable</td>
                              )}
                              <td>Quantity</td>
                            </tr>
                          </thead>
                          <tbody>
                            {kitContents.contents.map((kc, idxKc) => {
                              return (
                                <tr key={idxKc}>
                                  <td className='text-center'>
                                    {!kc.dontLinkToProduct ? (
                                      <Link
                                        href={`/part/${kc.stockCode}`}
                                        legacyBehavior
                                      >
                                        <a>{kc.stockCode}</a>
                                      </Link>
                                    ) : (
                                      <span>{kc.stockCode}</span>
                                    )}
                                  </td>
                                  <td
                                    className={
                                      showKitContentsInterchangeColumn
                                        ? ""
                                        : "text-center"
                                    }
                                  >
                                    {kc.description}
                                  </td>
                                  {showKitContentsInterchangeColumn && (
                                    <td className='text-center'>
                                      {kc.interchange}
                                    </td>
                                  )}
                                  <td className='text-center'>{kc.quantity}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </Accordion.Body>
                    </Accordion.Item>
                  )}
                  {publishMajorApplication && (
                    <Accordion.Item eventKey='5'>
                      <Accordion.Header>Application Summary</Accordion.Header>
                      <Accordion.Body>{data.webUses}</Accordion.Body>
                    </Accordion.Item>
                  )}
                  {showExtraInfo && (
                    <Accordion.Item eventKey='6'>
                      <Accordion.Header>Other Information</Accordion.Header>
                      <Accordion.Body>
                        <table className='table table-striped'>
                          <tbody>
                            {extraDownloads
                              .filter(downloadItem => downloadItem.file && downloadItem.file.length > 0)
                              .map((downloadItem, iddc) => (
                                <tr key={iddc}>
                                  <th>{downloadItem.title}</th>
                                  <td>
                                    <a
                                      href={downloadItem.file[0].url}
                                      target='_blank'
                                      rel='noreferrer'
                                    >
                                      {downloadItem.file[0].fileName}
                                      <Image
                                        src='/icons/download.svg'
                                        width='14'
                                        height='14'
                                        alt=''
                                        layout='raw'
                                      ></Image>
                                    </a>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </Accordion.Body>
                    </Accordion.Item>
                  )}
                </Accordion>
                {pageLinkUrl  && (
                  <div className="new-space py-md-3 py-3 py-lg-5">
                        <div className='text-center'>
                          See our full range of  <Link href={detailsUrl} > {detailsType}s</Link>
                        </div>
                        </div>
                        )}
              </div>
            </div>
            {stockPage && stockPage?.sections?.length > 0 && (
              <>
                <div className='container new-space py-md-3 py-3 py-lg-5 mb-md-3 mb-0 mb-lg-5 text-center'>
                  <div className='row'>
                    <SectionsRender sections={stockPage?.sections} />
                  </div>
                </div>
              </>
            )}
                         {data?.relatedProducts && data.relatedProducts.length > 0 && (
              <div className='container new-space py-md-3 py-3 py-lg-5 mb-md-3 mb-0 mb-lg-5 text-center'>
                <h3 className='mb-4'>RELATED PRODUCTS</h3>
                <div className='row'>
                  {data.relatedProducts.map(function (item, idx) {
                    return (
                      <div key={"rl-" + idx} className='col-sm-3 mb-3'>
                        <div className='product-catalog'>
                          <div className='product-img'>
                            <a href={"/part/" + item.productCode}>
                              <Image
                                src={item.imageUrl}
                                width='304'
                                height='223'
                                alt={
                                  "related product image " + item.productCode
                                }
                                layout='raw'
                              ></Image>
                            </a>
                          </div>
                          <div className='product-body'>
                            <a href={"/part/" + item.productCode}>
                              <span className='product-caption'>
                                {item.typeName} {item.productCode}
                              </span>
                            </a>
                          </div>
                        </div>
                        <p>
                          <sub>{item.note}</sub>
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
        <div id='modal-root'></div>
                 {data?.videoId && (
          <div className='mt-2'>
            <div className='container new-space text-center'>
              <Vimeo video={data.videoId} responsive={true}></Vimeo>
            </div>
          </div>
        )}
      </div>
      <ExtraPageFooter></ExtraPageFooter>
      <Footer {...footer}></Footer>
    </>
  );
}

export const getStaticProps = async ({ params }) => {
  const { id } = params;
  
  // Handle null/undefined id parameter
  if (!id) {
    return {
      notFound: true,
    };
  }
  
  const filter = `data/stockCode/iv eq '${id.toUpperCase()}'`;
  var data, footerContents, stockData, stockPage, menuContents, stockPageContent, product, filterPageMap;

  const loadPartFromCms = async () => {
    const qryData = await queryWithRetry({
      query: STOCK_QUERY,
      variables: { filter: filter, filterStockPage: filter },
      fetchPolicy: "cache-first",
    });
    return qryData.data;
  };

  if (process.env.NEXT_PHASE === PHASE_PRODUCTION_BUILD ) {    
    //check in local cache written by this build
    let cache = cacheClient();
    footerContents = cache.get("footerContents");
    menuContents = cache.get("menuContents");
    let cacheStockData = cache.get("stockData");
    let cacheStockPageData = cache.get("stockPage");  
    let cacheFilterPageMap = cache.get("filterPageMap");
    if (
      Array.isArray(cacheStockData) &&
      Array.isArray(cacheStockPageData) &&
      Array.isArray(cacheFilterPageMap) &&
      footerContents &&
      menuContents
    ) {
      stockData = cacheStockData.find(x => x.flatData?.stockCode == id.toUpperCase());
      product = stockData ? stockData.flatData : null;
      //console.log("=================================>>>>>>>>>>>>>>>>>>>");
      //console.log("find product in cache", id, product);
      stockPage = cacheStockPageData.filter(x => x.flatData?.stockCode == id.toUpperCase());
      filterPageMap = cacheFilterPageMap;
    } else {
      data = await loadPartFromCms();
      footerContents = data.queryFooterContents;
      filterPageMap = data.queryFilterPageMapContents;
      menuContents = data.queryMenuContents;
      stockData = data.queryStockDetailContents;
      stockPage = data.queryStockpagesContents;
      product = stockData.length > 0 ? stockData[0].flatData : null;
    }
  } else {
    data = await loadPartFromCms();
    footerContents = data.queryFooterContents;
    filterPageMap = data.queryFilterPageMapContents;
    menuContents = data.queryMenuContents;
    stockData = data.queryStockDetailContents;
    stockPage = data.queryStockpagesContents; 
    product = stockData.length > 0 ? stockData[0].flatData : null;
  }
  // console.log("query_data", filterPageMap);
  const footerContent =
    footerContents[0] != null ? footerContents[0].flatData : [];
  const menuContent = menuContents[0] != null ? menuContents[0].flatData : [];
  
  const notFound = !product;
  const url = `/part/${id ? id.toLowerCase() : ''}`;
  const isNzOnly = product ? !product.isAU && product.isNZ : false;
  let majorApplication=product?.majorApplicationSeo?product?.webUses:'';  
  let extraKeywords = product?.stockKeyword;
  if (!extraKeywords){
    extraKeywords = "";
  }else{
    extraKeywords = ", " + extraKeywords;
  }
  if (majorApplication){
    majorApplication = ", " + majorApplication;
  }
  const keywords = product
    ? `${product?.stockCode}, Ryco ${product?.stockCode} for Sale${majorApplication}${extraKeywords}`
    : "";
    if(stockPage){
      if(!stockPage.length){
        //stockPageContent=!stockPage[0].flatData?[]:!stockPage[0].flatData;
      }
      else
      stockPageContent=stockPage[0].flatData;
    }
    const cleanedDescription = product?.webDescription.replace(/Ryco/g, "").trim();
    const seoTitle = `${product?.stockCode} | ${cleanedDescription} - Ryco Filters`;
    const pageLinkUrl  = filterPageMap.find(item => item.flatData.filterTypeName && item.flatData.filterTypeName === cleanedDescription)?.flatData?.pageId;
    // console.log("cleanedDescription", cleanedDescription);
    //  console.log("Footer--pageLinkUrl", footerContent);
    // console.log("filterPageMap", filterPageMap);
  return {
    props: {
      data: product ?? {},
      stockPage:stockPageContent?stockPageContent:{},
      footer: footerContent,
      menu: menuContent,
      pageLinkUrl:pageLinkUrl|| null,
      detailsType:cleanedDescription||null,
      id: id,
      
      seo: {
        title: seoTitle,
        metadata: `${product?.webDescription} ${product?.stockCode} ${majorApplication}`,
        keywords: keywords,
        pic: product?.imageUrl
          ? product?.imageUrl.replace("WEBP", "PNG")
          : null,
        socialTitle: `${product?.webDescription} ${product?.stockCode}`,
        url: url,
        isNzOnly: isNzOnly,
      },
    },
    notFound: notFound,
    revalidate: 60 * 30, // in seconds
  };
};

export async function getStaticPaths() {
  const isAU = process.env.NEXT_PUBLIC_SITE_IS_AU == "true";
  const country = isAU ? "isAU" : "isNZ";
  const stockConditionFilter = isAU ? "stockConditionAU" : "stockConditionNZ";
  let filter = `data/${country}/iv eq true and data/typeName/iv ne null and data/${stockConditionFilter}/iv ne 'Hidden'`;

  const { data } = await queryWithRetry({
    query: STOCK_LIST_QUERY,
    variables: { filter: filter },
    fetchPolicy: "network-only",
  });

  const { queryStockDetailContents } = data;

  let stockCacheReady = false;
  if (process.env.NEXT_PHASE === PHASE_PRODUCTION_BUILD ) {    
    console.log("Loading all stock data");
    const cache = cacheClient();
    try {
      const dataAll = await queryWithRetry({
        query: STOCK_QUERY,
        variables: { filter: filter, filterStockPage: "data/stockCode/iv ne null" },
        fetchPolicy: "network-only",
      });
      const {
        queryFooterContents: footerContents,
        queryMenuContents: menuContents,
        queryStockDetailContents: stockData,
        queryStockpagesContents:stockPage,
        queryFilterPageMapContents:filterPageMap,

      } = dataAll.data;
      console.log("Save all stock data locally during production build");
      cache.set("footerContents", footerContents);
      cache.set("menuContents", menuContents);
      cache.set("stockData", stockData);
      cache.set("stockPage", stockPage); 
      cache.set("filterPageMap", filterPageMap);
      stockCacheReady = true;
    } catch (error) {
      stockCacheReady =
        Array.isArray(cache.get("stockData")) &&
        Array.isArray(cache.get("stockPage")) &&
        Array.isArray(cache.get("filterPageMap"));
      console.log(
        stockCacheReady
          ? "Failed to preload stock data. Part pages will use the existing local cache."
          : "Failed to preload stock data. Part pages will be generated on request.",
        error
      );
    }
  }  

  const mappedIndex = queryStockDetailContents.map((item) => ({
    params: {
      id: slugify(item.flatData.stockCode),
    },
  }));

  //console.log("Product Details getStaticPaths Called");

  return {
    paths:
      process.env.NEXT_PHASE === PHASE_PRODUCTION_BUILD && !stockCacheReady
        ? []
        : mappedIndex,
    fallback: "blocking",
  };
}

export default Part;
